import type { Item, Part, PartType, Site } from '../../domain/inventory';
import { siteLabel } from '../../lib/site-label';
import { siteCompleteness, type SiteCompleteness } from '../site-status/site-completeness';

export interface CustomerLocationSummary {
  countries: string[];
  regions: string[];
  locations: Array<{
    country: string;
    region: string;
  }>;
}

// Region/country live on Site, not Customer -- Customer.labels.region is
// never populated in practice (see https://github.com/tidaltech/koi/issues/7796).
// This derives a customer's actual footprint from its sites instead, so a
// customer with sites in several countries/regions is represented as such
// rather than always reading as "not recorded".
export function customerLocationSummary(
  customerId: string,
  sites: readonly Site[],
): CustomerLocationSummary {
  const countries = new Set<string>();
  const regions = new Set<string>();
  const locations = new Map<string, { country: string; region: string }>();
  for (const site of sites) {
    if (site.customerId !== customerId) continue;
    const country = site.labels?.country?.trim();
    const region = site.labels?.region?.trim() || site.region?.trim();
    if (country) countries.add(country);
    if (region) regions.add(region);
    if (country || region) {
      locations.set(`${country ?? ''}|${region ?? ''}`, {
        country: country ?? '',
        region: region ?? '',
      });
    }
  }
  return {
    countries: [...countries].sort((left, right) => left.localeCompare(right)),
    regions: [...regions].sort((left, right) => left.localeCompare(right)),
    locations: [...locations.values()].sort((left, right) => {
      const country = left.country.localeCompare(right.country);
      return country || left.region.localeCompare(right.region);
    }),
  };
}

export const siteGenerations = ['gen1', 'gen2', 'hybrid', 'unclassified'] as const;
export type SiteGeneration = (typeof siteGenerations)[number];

export const siteGenerationLabels: Record<SiteGeneration, string> = {
  gen1: 'Gen 1',
  gen2: 'Gen 2',
  hybrid: 'Hybrid',
  unclassified: 'Unclassified',
};

const GENERATION_COMPONENT_TYPES = new Set<PartType>([
  'CAMERA_SYSTEM',
  'WINCH_SYSTEM',
  'UNIT_CONTROLLER',
  'POWERBOX',
]);
const GEN1_REQUIRED_TYPES: PartType[] = ['CAMERA_SYSTEM', 'WINCH_SYSTEM', 'UNIT_CONTROLLER'];
const GEN2_REQUIRED_TYPES: PartType[] = ['CAMERA_SYSTEM', 'WINCH_SYSTEM', 'POWERBOX'];

type GenerationFamily = 'gen1' | 'gen2';

export interface GenerationComponentCounts {
  cameras: number;
  winches: number;
  unitControllers: number;
  powerboxes: number;
}

export interface SiteGenerationEvidence {
  generation: SiteGeneration;
  gen1Items: number;
  gen2Items: number;
  unrecognizedItems: number;
  gen1Components: GenerationComponentCounts;
  gen2Components: GenerationComponentCounts;
}

/**
 * Camera systems for a site or customer: active (assigned and ACTIVE),
 * assigned to the site(s), and ordered (Site.orderedSystems). Customers and
 * sites are ordered by these, biggest first.
 */
export interface CameraSystemCounts {
  active: number;
  assigned: number;
  ordered: number;
}

export function emptyCameraSystemCounts(): CameraSystemCounts {
  return { active: 0, assigned: 0, ordered: 0 };
}

export function addCameraSystemCounts(
  total: CameraSystemCounts,
  more: CameraSystemCounts,
): CameraSystemCounts {
  return {
    active: total.active + more.active,
    assigned: total.assigned + more.assigned,
    ordered: total.ordered + more.ordered,
  };
}

/** Most active camera systems first, then most assigned, then most ordered. */
export function compareCameraSystems(left: CameraSystemCounts, right: CameraSystemCounts): number {
  return (
    right.active - left.active || right.assigned - left.assigned || right.ordered - left.ordered
  );
}

/** Camera system counts per site id, for the given sites. */
export function cameraSystemCountsBySite(
  sites: readonly Site[],
  items: readonly Item[],
  parts: readonly Part[],
): Map<string, CameraSystemCounts> {
  const cameraParts = new Set(
    parts.filter((part) => part.type === 'CAMERA_SYSTEM').map((part) => part.id),
  );
  const counts = new Map<string, CameraSystemCounts>(
    sites.map((site) => [
      site.id,
      { ...emptyCameraSystemCounts(), ordered: site.orderedSystems ?? 0 },
    ]),
  );
  for (const item of items) {
    const site = counts.get(item.siteId);
    if (!site || !cameraParts.has(item.partId)) continue;
    site.assigned += 1;
    if (item.status === 'ACTIVE') site.active += 1;
  }
  return counts;
}

/** Camera system counts per customer id, summed over their sites. */
export function cameraSystemCountsByCustomer(
  sites: readonly Site[],
  items: readonly Item[],
  parts: readonly Part[],
): Map<string, CameraSystemCounts> {
  const bySite = cameraSystemCountsBySite(sites, items, parts);
  const out = new Map<string, CameraSystemCounts>();
  for (const site of sites) {
    out.set(
      site.customerId,
      addCameraSystemCounts(
        out.get(site.customerId) ?? emptyCameraSystemCounts(),
        bySite.get(site.id) ?? emptyCameraSystemCounts(),
      ),
    );
  }
  return out;
}

export interface CustomerSiteInsight extends SiteGenerationEvidence {
  site: Site;
  cameraSystems: CameraSystemCounts;
  /** Missing and extra parts against the site's systems. */
  completeness: SiteCompleteness;
  assignedItems: number;
  locatedItems: number;
  needsAttention: number;
  locationVariance: number;
}

export interface CustomerInsights {
  sites: CustomerSiteInsight[];
  assignedItems: number;
  locatedItems: number;
  needsAttention: number;
  locationVariance: number;
  generationCounts: Record<SiteGeneration, number>;
}

export interface CustomerSiteRegionGroup {
  key: string;
  label: string;
  sites: CustomerSiteInsight[];
}

export interface CustomerSiteCountryGroup {
  key: string;
  label: string;
  regions: CustomerSiteRegionGroup[];
}

function generationFamily(itemId: string): GenerationFamily | undefined {
  const normalized = itemId.trim().toLowerCase();
  if (normalized.startsWith('g8')) return 'gen1';
  if (normalized.startsWith('vv')) return 'gen2';
  return undefined;
}

function includesEveryType(
  typesByFamily: Record<GenerationFamily, Set<PartType>>,
  family: GenerationFamily,
  requiredTypes: readonly PartType[],
): boolean {
  return requiredTypes.every((type) => typesByFamily[family].has(type));
}

function emptyGenerationComponentCounts(): GenerationComponentCounts {
  return {
    cameras: 0,
    winches: 0,
    unitControllers: 0,
    powerboxes: 0,
  };
}

function recordGenerationComponent(counts: GenerationComponentCounts, partType: PartType): void {
  if (partType === 'CAMERA_SYSTEM') counts.cameras += 1;
  if (partType === 'WINCH_SYSTEM') counts.winches += 1;
  if (partType === 'UNIT_CONTROLLER') counts.unitControllers += 1;
  if (partType === 'POWERBOX') counts.powerboxes += 1;
}

export function classifySiteGeneration(
  assignedItems: readonly Item[],
  partsById: ReadonlyMap<string, Part>,
): SiteGenerationEvidence {
  const typesByFamily: Record<GenerationFamily, Set<PartType>> = {
    gen1: new Set<PartType>(),
    gen2: new Set<PartType>(),
  };
  let gen1Items = 0;
  let gen2Items = 0;
  let unrecognizedItems = 0;
  const gen1Components = emptyGenerationComponentCounts();
  const gen2Components = emptyGenerationComponentCounts();

  for (const item of assignedItems) {
    const partType = partsById.get(item.partId)?.type;
    if (!partType || !GENERATION_COMPONENT_TYPES.has(partType)) continue;

    const family = generationFamily(item.id);
    if (!family) {
      unrecognizedItems += 1;
      continue;
    }

    typesByFamily[family].add(partType);
    if (family === 'gen1') {
      gen1Items += 1;
      recordGenerationComponent(gen1Components, partType);
    } else {
      gen2Items += 1;
      recordGenerationComponent(gen2Components, partType);
    }
  }

  let generation: SiteGeneration = 'unclassified';
  if (gen1Items > 0 && gen2Items > 0) {
    generation = 'hybrid';
  } else if (
    gen1Items > 0 &&
    gen2Items === 0 &&
    unrecognizedItems === 0 &&
    includesEveryType(typesByFamily, 'gen1', GEN1_REQUIRED_TYPES)
  ) {
    generation = 'gen1';
  } else if (
    gen2Items > 0 &&
    gen1Items === 0 &&
    unrecognizedItems === 0 &&
    includesEveryType(typesByFamily, 'gen2', GEN2_REQUIRED_TYPES)
  ) {
    generation = 'gen2';
  }

  return {
    generation,
    gen1Items,
    gen2Items,
    unrecognizedItems,
    gen1Components,
    gen2Components,
  };
}

export function buildCustomerInsights(
  sites: readonly Site[],
  items: readonly Item[],
  parts: readonly Part[],
): CustomerInsights {
  const partsById = new Map(parts.map((part) => [part.id, part]));
  const siteIds = new Set(sites.map((site) => site.id));
  const generationCounts: Record<SiteGeneration, number> = {
    gen1: 0,
    gen2: 0,
    hybrid: 0,
    unclassified: 0,
  };

  const cameraSystems = cameraSystemCountsBySite(sites, items, parts);
  const siteInsights = sites.map((site) => {
    const assigned = items.filter((item) => item.siteId === site.id);
    const located = items.filter((item) => (item.locationSiteId ?? item.siteId) === site.id);
    const generationEvidence = classifySiteGeneration(assigned, partsById);
    generationCounts[generationEvidence.generation] += 1;

    return {
      site,
      cameraSystems: cameraSystems.get(site.id) ?? emptyCameraSystemCounts(),
      completeness: siteCompleteness(site, assigned, partsById),
      assignedItems: assigned.length,
      locatedItems: located.length,
      needsAttention: assigned.filter((item) => item.needsAttention).length,
      locationVariance: assigned.filter(
        (item) => (item.locationSiteId ?? item.siteId) !== item.siteId,
      ).length,
      ...generationEvidence,
    };
  });

  const assignedCustomerItems = items.filter((item) => siteIds.has(item.siteId));
  const locatedCustomerItems = items.filter((item) =>
    siteIds.has(item.locationSiteId ?? item.siteId),
  );

  return {
    sites: siteInsights,
    assignedItems: assignedCustomerItems.length,
    locatedItems: locatedCustomerItems.length,
    needsAttention: items.filter((item) => item.needsAttention).length,
    locationVariance: assignedCustomerItems.filter(
      (item) => (item.locationSiteId ?? item.siteId) !== item.siteId,
    ).length,
    generationCounts,
  };
}

export function groupCustomerSiteInsights(
  insights: readonly CustomerSiteInsight[],
): CustomerSiteCountryGroup[] {
  const countries = new Map<string, CustomerSiteCountryGroup>();
  const regions = new Map<string, CustomerSiteRegionGroup>();

  for (const insight of insights) {
    const country = insight.site.labels?.country?.trim() || 'Not recorded';
    const region =
      insight.site.labels?.region?.trim() || insight.site.region?.trim() || 'Not recorded';
    const countryKey = country;
    const regionKey = `${countryKey}:${region}`;
    const countryGroup = countries.get(countryKey) ?? {
      key: countryKey,
      label: country,
      regions: [],
    };
    const regionGroup = regions.get(regionKey) ?? {
      key: regionKey,
      label: region,
      sites: [],
    };
    regionGroup.sites.push(insight);
    regions.set(regionKey, regionGroup);
    if (!countryGroup.regions.some((candidate) => candidate.key === regionKey)) {
      countryGroup.regions.push(regionGroup);
    }
    countries.set(countryKey, countryGroup);
  }

  // Biggest first at every level: countries, regions and sites by camera
  // systems (active, assigned, ordered), then by name.
  const total = (list: readonly CustomerSiteInsight[]) =>
    list.reduce(
      (sum, insight) => addCameraSystemCounts(sum, insight.cameraSystems),
      emptyCameraSystemCounts(),
    );
  return [...countries.values()]
    .map((country) => ({
      ...country,
      regions: country.regions
        .map((region) => ({
          ...region,
          sites: [...region.sites].sort(
            (left, right) =>
              compareCameraSystems(left.cameraSystems, right.cameraSystems) ||
              siteLabel(left.site).localeCompare(siteLabel(right.site)),
          ),
        }))
        .sort(
          (left, right) =>
            compareCameraSystems(total(left.sites), total(right.sites)) ||
            left.label.localeCompare(right.label),
        ),
    }))
    .sort(
      (left, right) =>
        compareCameraSystems(
          total(left.regions.flatMap((region) => region.sites)),
          total(right.regions.flatMap((region) => region.sites)),
        ) || left.label.localeCompare(right.label),
    );
}
